import { useQuery } from "urql";
import {
  InterviewScriptItemsQuery,
  InterviewsQuery,
  TermInterviewsQuery,
} from "../graphql/interviews.graphql";

export interface CompletionReport {
  completionReportId: string;
  handoverNote: string | null;
  coachReportNg: boolean;
  interviewerInsight: string | null;
  internalUseAllowed: boolean | null;
  hasConfidentialInfo: boolean;
  confidentialInfo: string | null;
  satisfactionScore: number | null;
  behaviorChangeScore: number | null;
  completedAt: string | null;
}

export interface Interview {
  interviewId: string;
  candidateId: string;
  candidateName: string | null;
  candidateEmail: string | null;
  termId: string;
  termTitle: string | null;
  sponsorId: string;
  sponsorName: string | null;
  interviewerId: string;
  interviewerName: string | null;
  interviewerEmail: string | null;
  startTime: string;
  endTime: string;
  zoomMeetingId: string | null;
  zoomMeetingUrl: string | null;
  zoomRecordingViewUrl: string | null;
  /** 語る対象。ステークホルダーインタビューのときだけ入るので、種別の判定にも使う */
  stakeholderContext: {
    stakeholderSettingName: string;
    recipientNames: string[];
    /** 回答は要約シートからの取り込みが正で、ここが揃った時点がこのインタビューの終わり */
    answersImported: boolean;
  } | null;
  status: string;
  completionReport: CompletionReport | null;
  summaryCheckCompletedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Admin GraphQL とインタビュアー向け GraphQL で共通利用。 */
export interface ScriptItem {
  scriptItemId: string;
  scriptId: string;
  templateItemId: string | null;
  question: string;
  answer: string | null;
  memo: string | null;
  note: string | null;
  order: number;
  orderIndex: number;
  createdAt: string | null;
  updatedAt: string | null;
}

/** 管理者向け一覧・型の互換用。インタビュアー向けは `useMyInterviews.ts` の SummaryItem（leadershipType なし）。 */
export interface SummaryItem {
  summaryItemId: string;
  interviewId: string;
  category: string;
  content: string;
  useCaseId: string | null;
  leadershipType: string | null;
  order: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export function useInterviews() {
  const [result, reexecute] = useQuery({
    query: InterviewsQuery,
  });

  return {
    interviews: (result.data?.interviews.nodes as Interview[]) ?? [],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useTermInterviews(termId: string | null) {
  const [result, reexecute] = useQuery({
    query: TermInterviewsQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  return {
    interviews: (result.data?.termInterviews.nodes as Interview[]) ?? [],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export interface AdminScriptItem {
  scriptItemId: string;
  scriptId: string;
  category: string;
  timeEstimate: string | null;
  itemName: string;
  isRequired: boolean;
  question: string;
  answer: string | null;
  memo: string | null;
  note: string | null;
  order: number;
  orderIndex: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export function useInterviewScriptItemsGraphQL(interviewId: string | null) {
  const [result] = useQuery({
    query: InterviewScriptItemsQuery,
    variables: { interviewId: interviewId ?? "" },
    pause: !interviewId,
  });

  return {
    scriptItems: (result.data?.interviewScriptItems.nodes ??
      []) as AdminScriptItem[],
    loading: result.fetching,
    error: result.error?.message ?? null,
  };
}
